import { useRef, useState, useEffect } from "react";
import { C, FONT } from "./theme";
import { useModalA11y } from "../useModalA11y";

// Shared right-click/dropdown context menu for the Phase 1 design-system
// foundation (see linear-brewing-toast.md). Today every context menu in the
// app (Workspace's folder/email/lead/campaign/sequence menus, Mail Merge's
// template menu, etc.) is hand-rolled separately — same rough shape
// (fixed-position white box) but each with its own border-radius, shadow,
// font and hover behavior. This is the one shared version future menus
// adopt; migrating each existing call site onto it is later, separate work
// (Phase 4) — nothing currently working changes as part of this file.
//
// Anatomy matches Modal.js/Tooltip.js's new Apple/iOS tokens (C from
// ./theme, FONT), not the old Outlook-desktop menu look (sharp 4px
// corners, Segoe UI) still used by today's hand-rolled menus.
//
// Usage:
//   <ContextMenu open={!!menu} onClose={()=>setMenu(null)} x={menu?.x} y={menu?.y}>
//     <ContextMenuItem onClick={rename}>Rename Folder</ContextMenuItem>
//     <ContextMenuItem destructive onClick={del}>Delete Folder</ContextMenuItem>
//     <ContextMenuDivider/>
//     <ContextMenuItem onClick={markRead}>Mark All as Read</ContextMenuItem>
//   </ContextMenu>
export function ContextMenu({ open, onClose, x = 0, y = 0, width = 210, children }) {
  const ref = useRef(null);
  useModalA11y(open, onClose, ref);
  if (!open) return null;
  return (
    <>
      {/* full-page click/right-click catcher, same role as each existing
          menu's own backdrop click-to-close. z-index is comfortably above
          Modal.js's own backdrop (99999) — a context menu (e.g. a template
          row's right-click menu, MailMerge.js) can legitimately open while
          a Modal is already open behind it (e.g. the Template Library),
          and must render above that Modal's backdrop, not behind it. */}
      <div style={{ position: "fixed", inset: 0, zIndex: 100000 }} onClick={onClose} onContextMenu={e => { e.preventDefault(); onClose(); }} />
      <div
        ref={ref}
        tabIndex={-1}
        role="menu"
        onClick={e => e.stopPropagation()}
        style={{ position: "fixed", top: y, left: x, minWidth: width, zIndex: 100001, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.16), 0 2px 6px rgba(0,0,0,.06)", padding: 6, fontFamily: FONT, outline: "none" }}
      >
        {children}
      </div>
    </>
  );
}

export function ContextMenuItem({ onClick, disabled, destructive, icon, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "7px 10px", background: "transparent", border: "none", borderRadius: 7, fontSize: 13, textAlign: "left", color: disabled ? C.textFaint : destructive ? C.red : C.text, cursor: disabled ? "default" : "pointer", fontFamily: FONT }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = C.bgHover; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
    >
      {icon}
      <span style={{ flex: 1 }}>{children}</span>
    </button>
  );
}

export function ContextMenuDivider() {
  return <div style={{ height: 1, background: C.borderSoft, margin: "5px 4px" }} />;
}

// A ContextMenuItem that opens its own flyout of sub-items on hover (or a
// tap, for touch), the way "Junk" or "Move" do in real Outlook — first
// consumer is the email context menu's "Junk" entry (Block Sender / Never
// Block Sender / Never Block Sender's Domain / Not Junk / Junk E-mail
// Options...). `items` uses the same {label,fn,disabled,destructive,divider}
// shape every other context menu in this app already builds; `onDone` is
// called after any sub-item runs so the caller can close the parent menu
// too, matching how a normal ContextMenuItem's onClick already closes it.
export function ContextSubMenu({ label, disabled, items = [], onDone }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const closeTimer = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const clearHideTimer = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const show = () => {
    if (disabled) return;
    clearHideTimer();
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const flyoutWidth = 230;
      const overflowsRight = r.right + flyoutWidth > window.innerWidth;
      setPos({
        x: overflowsRight ? Math.max(4, r.left - flyoutWidth) : r.right,
        y: Math.min(r.top, window.innerHeight - 8),
      });
    }
    setOpen(true);
  };
  const scheduleHide = () => { clearHideTimer(); closeTimer.current = setTimeout(() => setOpen(false), 250); };
  useEffect(() => () => clearHideTimer(), []);
  return (
    <div onMouseEnter={show} onMouseLeave={scheduleHide} style={{ position: "relative" }}>
      <button
        ref={btnRef}
        disabled={disabled}
        onClick={e => { e.stopPropagation(); show(); }}
        style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "7px 10px", background: open ? C.bgHover : "transparent", border: "none", borderRadius: 7, fontSize: 13, textAlign: "left", color: disabled ? C.textFaint : C.text, cursor: disabled ? "default" : "pointer", fontFamily: FONT }}
      >
        <span style={{ flex: 1 }}>{label}</span>
        <span style={{ fontSize: 10, color: C.textFaint }}>▶</span>
      </button>
      {open && !disabled && (
        <div
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
          onClick={e => e.stopPropagation()}
          role="menu"
          style={{ position: "fixed", top: pos.y, left: pos.x, minWidth: 230, zIndex: 100002, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.16), 0 2px 6px rgba(0,0,0,.06)", padding: 6, fontFamily: FONT }}
        >
          {items.map((it, i) => it.divider
            ? <ContextMenuDivider key={i} />
            : (
              <ContextMenuItem
                key={i}
                disabled={it.disabled}
                destructive={it.destructive}
                onClick={() => { if (it.fn) it.fn(); setOpen(false); if (onDone) onDone(); }}
              >
                <span title={it.title || undefined}>{it.label}</span>
              </ContextMenuItem>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default ContextMenu;
